import { Err, Ok, ok, Result } from 'neverthrow';
import _ from 'lodash';

export function reduceResult<T, E, U = T>(
  arr: T[],
  callback: (previousValue: U, currentValue: T) => Result<U, E>,
  initialValue: U
): Result<U, E> {
  return arr.reduce((prev, curr) => {
    if (prev.isErr()) {
      return prev;
    } else {
      return callback(prev.value, curr);
    }
  }, ok(initialValue) as Result<U, E>);
}

export const partitionResults = <T, E>(
  results: Result<T, E>[]
): [okVals: T[], errVals: E[]] => {
  const [oks, errs] = _.partition(results, (res) => res.isOk());
  return [oks.map((o) => o.value), errs.map((e) => e.error)];
};

/**
 * Partitions result records into ok records and err records.
 */
export const partitionRecords = <T, E>(
  records: Record<string, Result<T, E>>
): [okRecords: Record<string, T>, errRecords: Record<string, E>] => {
  const entries = Object.entries(records);

  const okEntries = entries.filter(entryIsOk);
  const errEntries = entries.filter(entryIsErr);

  const okRecords = Object.fromEntries(
    okEntries.map(([key, val]) => [key, val.value])
  );

  const errRecords = Object.fromEntries(
    errEntries.map(([key, val]) => [key, val.error])
  );

  return [okRecords, errRecords];
};

const entryIsOk = <T, E>(
  entry: [string, Result<T, E>]
): entry is [string, Ok<T, E>] => entry[1].isOk();

const entryIsErr = <T, E>(
  entry: [string, Result<T, E>]
): entry is [string, Err<T, E>] => entry[1].isErr();
